import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileText, Mail, Paperclip, Plus, Receipt, Sparkles, Upload, Wallet } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { CONTRACTS_KEY, contractsApi } from '@/features/contracts/contracts-api';
import { SupplierDialog } from '@/features/suppliers/suppliers-page';
import { SUPPLIERS_KEY, suppliersApi } from '@/features/suppliers/suppliers-api';
import { ApiError, openAuthedFile } from '@/lib/api';
import { useAuth, useCanEdit } from '@/lib/auth';
import { useFeatures } from '@/lib/features';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { BILL_STATUS, BILLS_KEY, PAYMENT_METHODS, PO_KEY, purchasingApi, type Bill, type BillInput } from './purchasing-api';

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date());
const daysUntil = (d: string) => Math.round((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000);

// התווית: purchasing:bills.tabs.<מפתח>.
const TABS = [{ key: 'open' }, { key: 'PAID' }, { key: 'all' }] as const;

/** חשבוניות ספקים: העלאה (נקראת לבד) → בדיקה → אישור → תשלום. */
export function SupplierInvoicesPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['purchasing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const list = useQuery({ queryKey: BILLS_KEY, queryFn: ({ signal }) => purchasingApi.bills(signal) });
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('open');
  const [editing, setEditing] = useState<{ bill: Bill | null; duplicate?: boolean } | null>(null);
  const [paying, setPaying] = useState<Bill | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: BILLS_KEY });
  const onError = (title: string) => (e: unknown) => toast.error(title, e instanceof ApiError ? e.message : undefined);

  const upload = useMutation({
    mutationFn: (file: File) => purchasingApi.uploadBill(file),
    onSuccess: async ({ invoice, duplicateOf }) => {
      await refresh();
      setEditing({ bill: invoice, duplicate: !!duplicateOf });
    },
    onError: onError(t('bills.uploadFailed')),
  });
  const approve = useMutation({
    mutationFn: (id: string) => purchasingApi.approveBill(id),
    onSuccess: async () => {
      toast.success(t('bills.approved'));
      await refresh();
    },
    onError: onError(t('bills.approveFailed')),
  });

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('bills.fileTooLarge'));
      return;
    }
    upload.mutate(file);
  };

  const all = useMemo(() => list.data ?? [], [list.data]);
  const unpaid = all.filter((b) => b.status === 'RECEIVED' || b.status === 'APPROVED');
  const overdue = unpaid.filter((b) => b.dueDate && daysUntil(b.dueDate) < 0);
  const week = unpaid.filter((b) => b.dueDate && daysUntil(b.dueDate) >= 0 && daysUntil(b.dueDate) <= 7);
  const toApprove = all.filter((b) => b.status === 'RECEIVED');
  const sumOf = (bs: Bill[]) => bs.reduce((s, b) => s + (b.amount ? Number(b.amount) : 0), 0);

  const items = useMemo(() => {
    if (tab === 'all') return all;
    if (tab === 'PAID') return all.filter((b) => b.status === 'PAID');
    return all.filter((b) => b.status !== 'PAID');
  }, [all, tab]);

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('bills.title')}
        description={t('bills.description')}
        action={
          canEdit ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing({ bill: null })}>
                <Plus aria-hidden />
                {t('bills.manual')}
              </Button>
              <Button size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
                <Upload aria-hidden />
                {t('bills.upload')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <input
        ref={input}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="sr-only"
        aria-label={t('bills.pickFile')}
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {all.length ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label={t('bills.overdue')} value={formatCurrency(sumOf(overdue))} hint={t('bills.count', { count: overdue.length })} tone={overdue.length ? 'danger' : undefined} />
          <Stat label={t('bills.dueThisWeek')} value={formatCurrency(sumOf(week))} hint={t('bills.count', { count: week.length })} tone={week.length ? 'warning' : undefined} />
          <Stat label={t('bills.awaitingApproval')} value={String(toApprove.length)} hint={formatCurrency(sumOf(toApprove))} />
        </div>
      ) : null}

      <Card
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (canEdit) pick(e.dataTransfer.files[0]);
        }}
      >
        <div className="flex gap-1 border-b border-border p-2" role="tablist">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              role="tab"
              aria-selected={tab === tb.key}
              onClick={() => setTab(tb.key)}
              className={cn(
                'rounded-(--radius-md) px-3 py-1.5 text-sm transition-colors',
                tab === tb.key ? 'bg-accent-subtle font-medium text-accent' : 'text-fg-muted hover:bg-surface-hover',
              )}
            >
              {t(`bills.tabs.${tb.key}`)}
            </button>
          ))}
        </div>
        {list.isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-14 w-full" />
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title={tab === 'open' ? t('bills.emptyOpen') : t('bills.empty')}
            description={t('bills.emptyHint')}
            action={
              canEdit ? (
                <Button size="sm" onClick={() => input.current?.click()}>
                  <Upload aria-hidden />
                  {t('bills.upload')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((b) => {
              const left = b.dueDate ? daysUntil(b.dueDate) : null;
              const open = b.status === 'RECEIVED' || b.status === 'APPROVED';
              return (
                <li key={b.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                  <button type="button" className="min-w-0 flex-1 space-y-0.5 text-start" onClick={() => setEditing({ bill: b })}>
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-fg">{b.supplier?.name ?? t('bills.unknownSupplier')}</span>
                      {b.invoiceNumber ? <span className="tabular text-xs text-fg-muted">#{b.invoiceNumber}</span> : null}
                      <Badge tone={BILL_STATUS[b.status].tone}>{t(`billStatus.${b.status}`)}</Badge>
                      {b.fromEmail ? (
                        <Badge tone="neutral" title={b.sourceFrom ? t('bills.sentFrom', { from: b.sourceFrom }) : undefined}>
                          <Mail className="size-3" aria-hidden />
                          {t('bills.fromEmail')}
                        </Badge>
                      ) : null}
                      {b.mismatch ? (
                        <Badge tone="warning">
                          <AlertTriangle className="size-3" aria-hidden />
                          {b.mismatch === 'higher' ? t('bills.higherThanOrder') : t('bills.lowerThanOrder')}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="flex flex-wrap gap-x-4 text-2xs text-fg-subtle">
                      {b.amount ? <span className="tabular text-sm font-medium text-fg">{formatCurrency(Number(b.amount))}</span> : <span>{t('bills.noAmount')}</span>}
                      {b.dueDate ? (
                        <span className={cn(open && left !== null && left < 0 && 'font-medium text-danger', open && left !== null && left >= 0 && left <= 7 && 'font-medium text-warning')}>
                          {t('bills.dueBy', { date: formatDate(b.dueDate) })}
                          {open && left !== null ? (left < 0 ? t('bills.overdueDays', { count: -left }) : left === 0 ? t('bills.dueToday') : t('bills.daysLeft', { count: left })) : ''}
                        </span>
                      ) : null}
                      {b.purchaseOrder ? <span>{t('bills.order', { number: b.purchaseOrder.number })}</span> : null}
                      {b.contract ? <span>{b.contract.title}</span> : null}
                      {b.paidAt ? <span>{t('bills.paidOn', { date: formatDate(b.paidAt) })}{b.paymentMethod ? ` · ${b.paymentMethod}` : ''}</span> : null}
                    </p>
                  </button>
                  <div className="flex flex-wrap gap-1">
                    {b.hasFile ? (
                      <Button variant="ghost" size="sm" aria-label={t('bills.openFile')} onClick={() => void openAuthedFile(`/supplier-invoices/${b.id}/file`)}>
                        <Paperclip aria-hidden />
                      </Button>
                    ) : null}
                    {b.status === 'RECEIVED' && can('OWNER') ? (
                      <Button size="sm" variant="secondary" loading={approve.isPending && approve.variables === b.id} disabled={!b.supplierId || !b.amount} onClick={() => approve.mutate(b.id)} data-track="supplier-invoice.approve">
                        <CheckCircle2 aria-hidden />
                        {t('bills.approve')}
                      </Button>
                    ) : null}
                    {b.status === 'APPROVED' && canEdit ? (
                      <Button size="sm" onClick={() => setPaying(b)} data-track="supplier-invoice.pay">
                        <Wallet aria-hidden />
                        {t('bills.markPaid')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      {editing ? <BillDialog bill={editing.bill} duplicate={editing.duplicate} onClose={() => setEditing(null)} /> : null}
      {paying ? <PayDialog bill={paying} onClose={() => setPaying(null)} /> : null}
    </div>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: 'warning' | 'danger' }) {
  return (
    <Card>
      <CardContent className="space-y-0.5 py-4">
        <p className="text-xs text-fg-muted">{label}</p>
        <p className={cn('tabular text-xl font-semibold text-fg', tone === 'warning' && 'text-warning', tone === 'danger' && 'text-danger')}>{value}</p>
        <p className="text-2xs text-fg-subtle">{hint}</p>
      </CardContent>
    </Card>
  );
}

const NONE = '__none__';

function BillDialog({ bill, duplicate, onClose }: { bill: Bill | null; duplicate?: boolean; onClose: () => void }) {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['purchasing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const features = useFeatures();
  const suppliers = useQuery({ queryKey: SUPPLIERS_KEY, queryFn: ({ signal }) => suppliersApi.list(signal) });
  const orders = useQuery({ queryKey: PO_KEY, queryFn: ({ signal }) => purchasingApi.orders(signal), enabled: features.has('purchaseOrders') });
  const contracts = useQuery({ queryKey: CONTRACTS_KEY, queryFn: ({ signal }) => contractsApi.list(signal), enabled: features.has('contracts') });
  const [addingSupplier, setAddingSupplier] = useState(false);
  const statusLocked = bill?.status === 'APPROVED' || bill?.status === 'PAID';
  // צפייה בלבד: הכל נעול, בלי שמירה.
  const locked = statusLocked || !canEdit;
  const ex = bill?.extracted;
  const [v, setV] = useState({
    supplierId: bill?.supplierId ?? NONE,
    invoiceNumber: bill?.invoiceNumber ?? '',
    issueDate: bill?.issueDate ?? '',
    dueDate: bill?.dueDate ?? '',
    amount: bill?.amount ?? '',
    vatAmount: bill?.vatAmount ?? '',
    purchaseOrderId: bill?.purchaseOrderId ?? NONE,
    contractId: bill?.contractId ?? NONE,
    notes: bill?.notes ?? '',
  });
  const set = <K extends keyof typeof v>(k: K, x: (typeof v)[K]) => setV((p) => ({ ...p, [k]: x }));
  const supplierOrders = (orders.data ?? []).filter((o) => v.supplierId !== NONE && o.supplier.id === v.supplierId && o.status !== 'CANCELLED');
  const supplierContracts = (contracts.data ?? []).filter((c) => v.supplierId !== NONE && c.supplierId === v.supplierId);

  const save = useMutation({
    mutationFn: () => {
      const num = (x: string) => (x === '' ? null : Math.round(Number(x) * 100) / 100);
      const body: BillInput = statusLocked
        ? { notes: v.notes.trim() || null }
        : {
            supplierId: v.supplierId === NONE ? null : v.supplierId,
            invoiceNumber: v.invoiceNumber.trim() || null,
            issueDate: v.issueDate || null,
            dueDate: v.dueDate || null,
            amount: num(v.amount),
            vatAmount: num(v.vatAmount),
            purchaseOrderId: v.purchaseOrderId === NONE ? null : v.purchaseOrderId,
            contractId: v.contractId === NONE ? null : v.contractId,
            notes: v.notes.trim() || null,
          };
      return bill ? purchasingApi.updateBill(bill.id, body) : purchasingApi.createBill(body);
    },
    onSuccess: async ({ invoice, duplicateOf }) => {
      if (duplicateOf) toast.info(t('billDialog.duplicateSaved'), t('billDialog.duplicateCheck'));
      else toast.success(t('billDialog.saved'), invoice.mismatch ? t('billDialog.mismatchHint') : undefined);
      await qc.invalidateQueries({ queryKey: BILLS_KEY });
      await qc.invalidateQueries({ queryKey: PO_KEY });
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const readByAi = !!ex && (ex.confidence ?? 0) > 0;

  return (
    <>
      <Dialog open={!addingSupplier} onOpenChange={(open) => !open && onClose()}>
        <DialogContent aria-describedby="bill-desc" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{bill ? (bill.supplier ? t('billDialog.titleWithSupplier', { supplier: bill.supplier.name }) : t('billDialog.title')) : t('billDialog.newTitle')}</DialogTitle>
            <DialogDescription id="bill-desc">
              {statusLocked ? t('billDialog.lockedDescription') : t('billDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {readByAi ? (
              <p className="flex items-start gap-1.5 rounded-(--radius-md) bg-info-subtle px-3 py-2 text-xs text-info">
                <Sparkles className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {t('billDialog.readByAi', {
                  supplier:
                    ex?.supplierName && !bill?.supplierId
                      ? t('billDialog.supplierInFile', { name: ex.supplierName, taxId: ex.supplierTaxId ? t('billDialog.taxId', { taxId: ex.supplierTaxId }) : '' })
                      : '',
                })}
              </p>
            ) : bill?.hasFile && !readByAi ? (
              <p className="rounded-(--radius-md) bg-surface-sunken px-3 py-2 text-xs text-fg-muted">{t('billDialog.readFailed')}</p>
            ) : null}
            {duplicate ? (
              <p className="flex items-center gap-1.5 rounded-(--radius-md) bg-warning-subtle px-3 py-2 text-xs text-warning">
                <AlertTriangle className="size-3.5" aria-hidden />
                {t('billDialog.duplicate')}
              </p>
            ) : null}
            {bill?.mismatch ? (
              <p className="flex items-center gap-1.5 rounded-(--radius-md) bg-warning-subtle px-3 py-2 text-xs text-warning">
                <AlertTriangle className="size-3.5" aria-hidden />
                {t(bill.mismatch === 'higher' ? 'billDialog.mismatchHigher' : 'billDialog.mismatchLower', {
                  number: bill.purchaseOrder?.number,
                  total: formatCurrency(Number(bill.purchaseOrder?.total ?? 0)),
                })}
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('billDialog.supplier')} htmlFor="bill-supplier">
                <div className="flex gap-2">
                  <Select value={v.supplierId} onValueChange={(x) => set('supplierId', x)} disabled={locked}>
                    <SelectTrigger id="bill-supplier" aria-label={t('billDialog.supplier')} className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('billDialog.notSelected')}</SelectItem>
                      {suppliers.data?.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!locked ? (
                    <Button type="button" variant="secondary" size="sm" aria-label={t('billDialog.newSupplier')} onClick={() => setAddingSupplier(true)}>
                      <Plus aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </Field>
              <Field label={t('billDialog.invoiceNumber')} htmlFor="bill-number">
                <Input id="bill-number" dir="ltr" maxLength={64} disabled={locked} value={v.invoiceNumber} onChange={(e) => set('invoiceNumber', e.target.value)} />
              </Field>
              <Field label={t('billDialog.issueDate')} htmlFor="bill-issue">
                <Input id="bill-issue" type="date" dir="ltr" disabled={locked} value={v.issueDate} onChange={(e) => set('issueDate', e.target.value)} />
              </Field>
              <Field label={t('billDialog.dueDate')} htmlFor="bill-due">
                <Input id="bill-due" type="date" dir="ltr" disabled={locked} value={v.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
              </Field>
              <Field label={t('billDialog.amount')} htmlFor="bill-amount">
                <Input id="bill-amount" type="number" inputMode="decimal" dir="ltr" min={0} step="0.01" disabled={locked} value={v.amount} onChange={(e) => set('amount', e.target.value)} />
              </Field>
              <Field label={t('billDialog.vat')} htmlFor="bill-vat">
                <Input id="bill-vat" type="number" inputMode="decimal" dir="ltr" min={0} step="0.01" disabled={locked} value={v.vatAmount} onChange={(e) => set('vatAmount', e.target.value)} />
              </Field>
              {features.has('purchaseOrders') ? (
                <Field label={t('billDialog.order')} htmlFor="bill-po" hint={v.supplierId === NONE ? t('billDialog.pickSupplierFirst') : undefined}>
                  <Select value={v.purchaseOrderId} onValueChange={(x) => set('purchaseOrderId', x)} disabled={locked || v.supplierId === NONE}>
                    <SelectTrigger id="bill-po" aria-label={t('billDialog.orderAria')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('billDialog.noOrder')}</SelectItem>
                      {supplierOrders.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          #{o.number} · {formatCurrency(Number(o.total))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : null}
              {features.has('contracts') ? (
                <Field label={t('billDialog.contract')} htmlFor="bill-contract">
                  <Select value={v.contractId} onValueChange={(x) => set('contractId', x)} disabled={locked || v.supplierId === NONE}>
                    <SelectTrigger id="bill-contract" aria-label={t('billDialog.contractAria')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('billDialog.noContract')}</SelectItem>
                      {supplierContracts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : null}
              <Field label={t('billDialog.notes')} htmlFor="bill-notes" className="sm:col-span-2">
                <Textarea id="bill-notes" rows={2} maxLength={2000} disabled={!canEdit} value={v.notes} onChange={(e) => set('notes', e.target.value)} />
              </Field>
            </div>
            {bill?.hasFile ? (
              <Button variant="link" size="sm" onClick={() => void openAuthedFile(`/supplier-invoices/${bill.id}/file`)}>
                <FileText aria-hidden />
                {bill.fileName ?? t('billDialog.defaultFileName')}
              </Button>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('common:actions.close')}
            </Button>
            {canEdit ? (
              <Button loading={save.isPending} onClick={() => save.mutate()}>
                {t('common:actions.save')}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {addingSupplier ? <SupplierDialog supplier={null} onClose={() => setAddingSupplier(false)} onSaved={(s) => set('supplierId', s.id)} /> : null}
    </>
  );
}

function PayDialog({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const { t } = useTranslation(['purchasing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [paidAt, setPaidAt] = useState(today());
  // נשמר כטקסט — התווית בשפת הממשק.
  const [method, setMethod] = useState<string>(t('paymentMethods.bankTransfer'));
  const pay = useMutation({
    mutationFn: () => purchasingApi.payBill(bill.id, { paidAt, paymentMethod: method }),
    onSuccess: async () => {
      toast.success(t('payDialog.marked'));
      await qc.invalidateQueries({ queryKey: BILLS_KEY });
      onClose();
    },
    onError: (e) => toast.error(t('payDialog.updateFailed'), e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="pay-desc">
        <DialogHeader>
          <DialogTitle>
            {t('payDialog.title', { supplier: bill.supplier?.name ?? t('payDialog.defaultSupplier') })}
            {bill.amount ? ` · ${formatCurrency(Number(bill.amount))}` : ''}
          </DialogTitle>
          <DialogDescription id="pay-desc">
            {t('payDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label={t('payDialog.date')} htmlFor="pay-date">
            <Input id="pay-date" type="date" dir="ltr" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </Field>
          <Field label={t('payDialog.method')} htmlFor="pay-method">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="pay-method" aria-label={t('payDialog.method')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={t(`paymentMethods.${m}`)}>
                    {t(`paymentMethods.${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!paidAt} loading={pay.isPending} onClick={() => pay.mutate()}>
            {t('payDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
